import { useMutation } from '@tanstack/react-query'
import type { RcFile } from 'antd/es/upload'

import {
  attachmentsCreateAvatarSignedUrlCreateMutation,
  attachmentsWebhookCreateMutation,
  type CreateAvatarUploadUrl,
  type WebhookData
} from 'src/api/main'

/**
 * The upload's `Content-Type` is part of the presigned signature, and the
 * backend guesses it from `title` unless the client declares it. The avatar is
 * re-encoded before it is sent, so only the client knows the real type.
 *
 * Not in `CreateAvatarUploadUrl`: the field is new, so the generated body type
 * does not know it yet. Drop this cast after the next `npm run openapi` —
 * see AGENTS.md.
 */
type DeclaredUploadUrl = CreateAvatarUploadUrl & { content_type?: string }

type UploadResult = {
  id: number
  key: string
  webhook_data: WebhookData
  upload_url: string
}

export const useAvatarUpload = () => {
  const createUrlMutation = useMutation(
    attachmentsCreateAvatarSignedUrlCreateMutation()
  )
  const webhookMutation = useMutation(attachmentsWebhookCreateMutation())

  const uploadFile = async (file: RcFile): Promise<UploadResult> => {
    const body: DeclaredUploadUrl = {
      title: file?.name,
      ...(file?.type ? { content_type: file.type } : {})
    }

    const final = await createUrlMutation.mutateAsync({ body })

    return {
      id: final?.id,
      key: final?.webhook_data.Key,
      webhook_data: final?.webhook_data,
      upload_url: final?.upload_url
    }
  }

  const submitWebhook = async (webhook_data: WebhookData): Promise<unknown> => {
    const final = await webhookMutation.mutateAsync({ body: webhook_data })

    return final
  }

  return {
    uploadFile,
    submitWebhook,
    isLoading: createUrlMutation.isPending || webhookMutation.isPending
  }
}
